import { useState, useRef, useEffect } from 'react'
import { IconSearch, IconX, IconMapPin } from '@tabler/icons-react'
import { cn } from 'src/enviroment/lib/utils'
import { mapboxRouter, type GeocodingFeature } from 'src/api/routes/mapbox'
import { Input } from 'src/components/atoms/inputs/Input'
import Typography from 'src/components/atoms/Typography'
import { ErrorMessage } from 'src/components/atoms/ErrorMessage'

interface CountryPickerProps {
  /** Selected country name (null = global). */
  value: string | null
  onChange: (country: string | null) => void
  label?: string
  errorMessage?: string
  className?: string
}

const CountryPicker = ({ value, onChange, label, errorMessage, className }: CountryPickerProps) => {
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300)
    return () => clearTimeout(timer)
  }, [query])

  const { data: results = [], isFetching } = mapboxRouter.geocode.useQuery(debouncedQuery, 'country')

  useEffect(() => {
    setIsOpen(results.length > 0)
  }, [results])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (feature: GeocodingFeature) => {
    onChange(feature.place_name)
    setQuery('')
    setIsOpen(false)
  }

  const handleClear = () => {
    onChange(null)
    setQuery('')
    setIsOpen(false)
  }

  return (
    <div className="space-y-2">
      {label && (
        <Typography variant="sm-medium" element="label" className="block text-gray-50">
          {label}
        </Typography>
      )}
      <div ref={containerRef} className={cn('relative', className)}>
        {value ? (
          <div className="flex h-[48px] w-full items-center justify-between rounded-lg border border-gray-300 bg-gray-800 px-4">
            <span className="flex items-center gap-2 text-sm-medium text-gray-50">
              <IconMapPin size={16} stroke={1.5} className="text-light-green-500" />
              {value}
            </span>
            <button
              type="button"
              onClick={handleClear}
              className="grid h-6 w-6 place-items-center rounded-full text-gray-300 transition-colors hover:bg-gray-700 hover:text-white"
            >
              <IconX size={14} stroke={1.5} />
            </button>
          </div>
        ) : (
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search country (leave empty for global)"
            icon={<IconSearch className="text-light-green-500" />}
            isLoading={isFetching}
            className="h-auto border-gray-700 bg-gray-800 py-3 text-gray-50"
            autoComplete="off"
          />
        )}

        {isOpen && !value && results.length > 0 && (
          <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-60 overflow-y-auto rounded-lg border border-gray-700 bg-gray-800 p-1 shadow-xl">
            {results.map((feature) => (
              <button
                key={feature.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(feature)}
                className="flex w-full items-center gap-2 rounded px-3 py-2 text-left transition-colors hover:bg-gray-700"
              >
                <IconMapPin size={14} stroke={1.5} className="shrink-0 text-gray-400" />
                <span className="truncate text-sm text-gray-300">{feature.place_name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {errorMessage && <ErrorMessage message={errorMessage} />}
    </div>
  )
}

export default CountryPicker
